import { CompanyContext } from '../context/CompanyContext';
import { useContext, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';

export const useCompanyContext = () => {
  const context = useContext(CompanyContext);
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);

  // voorkomt dubbele runs/requests
  const didInitRef = useRef(false);
  const inFlightByIdRef = useRef({});

  if (!context) {
    throw Error('useCompanyContext must be used inside an CompanyContextProvider');
  }

  // Kies ID uit storage (per user) of uit firstCompany
  const pickSelectedId = () => {
    if (!user) return null;
    const storageKey = `selectedCompany:${user.email?.toLowerCase()}`;
    const stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    return stored?.Companyid ?? user?.firstCompany?.Companyid ?? null;
  };

  // 1) INIT: zet minimaal bedrijf (alleen id+name) in context/storage, éénmalig
  useEffect(() => {
    if (didInitRef.current) return;
    if (!user) {
      setLoading(false);
      return;
    }
    const selectedId = context.company?.Companyid || pickSelectedId();
    if (!selectedId) {
      setLoading(false);
      didInitRef.current = true;
      return;
    }
    // als nog niets in context staat: zet minimal nu, zodat select/ UI klopt
    if (!context.company?.Companyid) {
      const name =
        user?.companies?.find(c => c.Companyid === selectedId)?.Name ||
        user?.firstCompany?.Name ||
        '';
      const minimal = { Companyid: selectedId, Name: name };
      context.dispatch({ type: 'SET_COMPANY', payload: minimal });

      const storageKey = `selectedCompany:${user.email?.toLowerCase()}`;
      sessionStorage.setItem(storageKey, JSON.stringify(minimal));
      sessionStorage.setItem('selectedCompany', JSON.stringify(minimal)); // legacy
    }
    didInitRef.current = true;
    // we laten loading pas op false gaan na hydratatie poging in effect 2
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, user?.firstCompany?.Companyid, context.company?.Companyid, context.dispatch]);

  // 2) HYDRATE: haal VOLLEDIGE company op als er geen companyinfo is
  useEffect(() => {
    const run = async () => {
      const id = context.company?.Companyid || pickSelectedId();
      if (!user?.token || !id) {
        setLoading(false);
        return;
      }

      const hasDetails =
        Array.isArray(context.company?.companyinfo) &&
        context.company.companyinfo.length > 0;

      if (hasDetails) {
        setLoading(false);
        return;
      }

      // al bezig met dit id?
      if (inFlightByIdRef.current[id]) return;
      inFlightByIdRef.current[id] = true;

      try {
        console.log('🏢 [CompanyContext] ophalen bedrijf:', id);

        const base = process.env.REACT_APP_API_URL;
        const headers = { Authorization: `Bearer ${user.token}` };

        // Probeer beide padvarianten
        const paths = [
          `/api/user/bedrijf/${id}`,
          `/api/user/company/${id}`,
        ];

        let fullCompany = null;
        let lastStatus = null;

        for (const path of paths) {
          try {
            const resp = await fetch(`${base}${path}`, { headers });
            lastStatus = resp.status;
            console.log(`[CompanyContext] GET ${path} -> ${resp.status}`);
            if (resp.ok) {
              fullCompany = await resp.json();
              break;
            }
          } catch (e) {
            console.warn(`[CompanyContext] request fout voor ${path}:`, e);
          }
        }

        if (!fullCompany?.Companyid) {
          console.error('❌ [CompanyContext] geen volledige company ontvangen (laatste status:', lastStatus, ')');
          setLoading(false);
          return;
        }

        context.dispatch({ type: 'SET_COMPANY', payload: fullCompany });

        // storage bijwerken (minimal)
        const minimal = { Companyid: fullCompany.Companyid, Name: fullCompany.Name };
        const storageKey = `selectedCompany:${user.email?.toLowerCase()}`;
        sessionStorage.setItem(storageKey, JSON.stringify(minimal));
        sessionStorage.setItem('selectedCompany', JSON.stringify(minimal)); // legacy

        console.log('✅ [CompanyContext] bedrijf gezet:', fullCompany.Companyid);
      } finally {
        setLoading(false);
        inFlightByIdRef.current[id] = false;
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.token,
    user?.email,
    user?.firstCompany?.Companyid,
    context.company?.Companyid,
    context.company?.companyinfo?.length,
    context.dispatch,
  ]);

  return { ...context, loading };
};
